import React, { useMemo } from 'react';
import { Box, Card, CardContent, Chip, LinearProgress, Typography } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import apiService from '../../services/api';
import type { DashboardChartFilters } from '../../types/dashboardStats';
import { DASHBOARD_BRAND_PRIMARY } from '../../theme/dashboardBrand';
import { formatDashboardPeriodLabel, formatDashboardRangeLabel } from '../../utils/dashboardChartUtils';

type SeriesKey = {
  key: string;
  label: string;
  color: string;
};

type Props = {
  title: string;
  filters: DashboardChartFilters;
  disabled?: boolean;
  queryKey: string;
  queryFn: (filters: DashboardChartFilters) => Promise<{
    total: number;
    date_from: string;
    date_to: string;
    city: string | null;
    series: Array<Record<string, string | number>>;
    revenue_eur?: number;
  }>;
  seriesKeys: SeriesKey[];
  totalLabel?: (total: number, data?: { revenue_eur?: number }) => string;
  subtitle?: (filters: DashboardChartFilters, data?: { date_from: string; date_to: string; city: string | null }) => string;
};

const DashboardStackedChartCard: React.FC<Props> = ({
  title,
  filters,
  disabled,
  queryKey,
  queryFn,
  seriesKeys,
  totalLabel,
  subtitle,
}) => {
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [queryKey, filters.granularity, filters.date_from, filters.date_to, filters.city ?? ''],
    queryFn: () => queryFn(filters),
    enabled: !disabled,
  });

  const chartData = useMemo(
    () =>
      (data?.series ?? []).map((point) => ({
        ...point,
        label: formatDashboardPeriodLabel(String(point.period), filters.granularity),
      })),
    [data?.series, filters.granularity],
  );

  const defaultSubtitle = data
    ? [
        filters.granularity === 'hour'
          ? `Répartition horaire sur ${formatDashboardRangeLabel(data.date_from, data.date_to)}`
          : formatDashboardRangeLabel(data.date_from, data.date_to),
        data.city ?? 'Toutes les villes',
      ].join(' · ')
    : '';

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" flexWrap="wrap" alignItems="flex-start" justifyContent="space-between" gap={2} mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle ? subtitle(filters, data) : defaultSubtitle}
            </Typography>
          </Box>
          {data && (
            <Chip
              label={totalLabel ? totalLabel(data.total, data) : `${data.total.toLocaleString('fr-FR')} total`}
              sx={{ bgcolor: DASHBOARD_BRAND_PRIMARY, color: '#fff', fontWeight: 700 }}
            />
          )}
        </Box>

        {(isLoading || isFetching) && <LinearProgress sx={{ mb: 2 }} />}

        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            Impossible de charger les statistiques.
          </Typography>
        )}

        {!isLoading && !error && data?.total === 0 && (
          <Box textAlign="center" py={6}>
            <Typography color="text.secondary">Aucune donnée sur cette période.</Typography>
          </Box>
        )}

        {!isLoading && !error && (data?.total ?? 0) > 0 && (
          <Box sx={{ width: '100%', height: 360 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12 }}
                  interval={
                    filters.granularity === 'hour'
                      ? 1
                      : chartData.length > 20
                        ? Math.floor(chartData.length / 12)
                        : 0
                  }
                  angle={chartData.length > 14 && filters.granularity !== 'hour' ? -35 : 0}
                  textAnchor={chartData.length > 14 && filters.granularity !== 'hour' ? 'end' : 'middle'}
                  height={chartData.length > 14 && filters.granularity !== 'hour' ? 56 : 30}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} width={40} />
                <Tooltip
                  formatter={(value, name) => {
                    const series = seriesKeys.find((s) => s.key === name);
                    return [Number(value ?? 0).toLocaleString('fr-FR'), series?.label ?? String(name)];
                  }}
                  labelFormatter={(_, payload) => {
                    const row = payload?.[0]?.payload as { period?: string } | undefined;
                    return row?.period ? formatDashboardPeriodLabel(row.period, filters.granularity) : '';
                  }}
                />
                <Legend formatter={(value) => seriesKeys.find((s) => s.key === value)?.label ?? value} />
                {seriesKeys.map((series, index) => (
                  <Bar
                    key={series.key}
                    dataKey={series.key}
                    stackId="stack"
                    fill={series.color}
                    name={series.key}
                    radius={index === seriesKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DashboardStackedChartCard;
